import { ExternalLink, Mail, Globe } from 'lucide-react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { destinations } from '../data/destinations';

interface FooterLink {
  label: string;
  // A route on this site, or an absolute URL for the sister product.
  to: string;
}

const isExternal = (to: string): boolean => to.startsWith('https://');

// Every page carries the footer, so these columns give each section of the
// site a crawl path one click from anywhere, not just from the sitemap.
const FOOTER_COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: 'Voyara AI',
    links: [
      { label: 'Explore destinations', to: '/explore' },
      { label: 'Travel intelligence', to: '/travel-intelligence' },
      { label: 'Corporate travel', to: '/corporate-travel' },
      { label: 'Knowledge lexicon', to: '/knowledge' },
    ],
  },
  {
    heading: 'Destinations',
    links: destinations.map((destination) => ({
      label: destination.name,
      to: `/destinations/${destination.slug}`,
    })),
  },
  {
    heading: 'WavesFlo, also by Eclipse',
    links: [
      { label: 'WhatsApp campaign features', to: 'https://www.wavesflo.com/features' },
      { label: 'WhatsApp marketing guides', to: 'https://www.wavesflo.com/blog' },
      { label: 'WavesFlo FAQ', to: 'https://www.wavesflo.com/faq' },
      { label: 'WavesFlo vs other WhatsApp tools', to: 'https://www.wavesflo.com/compare' },
    ],
  },
];

const FOOTER_LINK_CLASS = 'text-[13px] text-[#f5f5f5]/70 hover:text-[#f5f5f5] transition-colors';

export default function Footer() {
  const ref = useRef(null);

  return (
    <footer ref={ref} className="relative">
      {/* Top fade line */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#f5f5f5]/[0.04] to-transparent" />

      <div className="max-w-7xl mx-auto px-8 md:px-16 py-16">
        <div className="flex flex-col md:flex-row items-center justify-between gap-10">
          {/* Left */}
          <div>
            <span className="font-serif text-lg text-[#f5f5f5]/50 tracking-tight">Eclipse Group</span>
          </div>

          {/* Center */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] text-[#f5f5f5]/55 font-light tracking-[0.05em]">Voyara AI</span>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-0.5">
              <Link to="/knowledge" className="text-[11px] text-[#f5f5f5]/75 hover:text-[#f5f5f5] uppercase tracking-[0.25em] transition-colors font-medium cursor-pointer py-1.5 px-2">
                Lexicon
              </Link>
              <span className="text-[9px] text-[#f5f5f5]/10 select-none">•</span>
              <Link to="/privacy" className="text-[11px] text-[#f5f5f5]/75 hover:text-[#f5f5f5] uppercase tracking-[0.25em] transition-colors font-medium cursor-pointer py-1.5 px-2">
                Privacy
              </Link>
              <span className="text-[9px] text-[#f5f5f5]/10 select-none">•</span>
              <Link to="/about" className="text-[11px] text-[#f5f5f5]/75 hover:text-[#f5f5f5] uppercase tracking-[0.25em] transition-colors font-medium cursor-pointer py-1.5 px-2">
                About
              </Link>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-4">
            {[
              { Icon: Mail, href: 'mailto:contact@eclipsegroup.in', label: 'Email' },
              { Icon: Globe, href: 'https://www.eclipsegroup.in', label: 'Website' },
              { Icon: ExternalLink, href: 'https://www.wavesflo.com', label: 'WavesFlo, also by Eclipse Group' },
            ].map((link, i) => (
              <a
                key={i}
                href={link.href}
                aria-label={link.label}
                className="w-11 h-11 rounded-full bg-[#f5f5f5]/[0.02] border border-[#f5f5f5]/[0.05] flex items-center justify-center hover:bg-[#f5f5f5]/[0.06] hover:border-[#f5f5f5]/[0.1] hover:shadow-[0_0_15px_rgba(120,140,255,0.04)] transition-all duration-500"
              >
                <link.Icon className="w-4.5 h-4.5 text-[#f5f5f5]/55 hover:text-[#f5f5f5]/80 transition-colors duration-400" />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Footer" className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 max-w-3xl mx-auto">
          {FOOTER_COLUMNS.map((column) => (
            <div key={column.heading}>
              <p className="text-[11px] text-[#f5f5f5]/55 uppercase tracking-[0.25em] font-medium mb-4">{column.heading}</p>
              <ul className="space-y-2.5 list-none p-0 m-0">
                {column.links.map((link) => (
                  <li key={link.to}>
                    {isExternal(link.to) ? (
                      <a href={link.to} className={FOOTER_LINK_CLASS}>{link.label}</a>
                    ) : (
                      <Link to={link.to} className={FOOTER_LINK_CLASS}>{link.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div className="mt-14 text-center">
          <p className="text-[11px] text-[#f5f5f5]/55 font-light tracking-[0.15em] uppercase">
            &copy; {new Date().getFullYear()} Eclipse Private Limited. All rights reserved. <br className="md:hidden" />
            <span className="opacity-50">Supported by Eclipse Media.</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
